// src/shared/auth/guards.js
//
// Unified navigation guard — handles public routes, auth check, RBAC,
// feature toggles, and role-based home routing.
//
// meta.requiredRole values are ROLE_KEYS (e.g. 'INSTRUCTOR', 'SUPER_ADMIN').
// rbac helpers resolve them to wire values via catalog.keyMap at runtime.

import { IframeUtil } from '@/shared/http/iframe';
import {
  hasMinRole,
  hasRole,
  hasAdminAccess,
  normalizeRole,
} from '@/shared/auth/rbac';
import { AI_PACKAGE_FEATURE_KEYS, AI_PACKAGE_FLAGS } from '@/shared/constants/packageFeatures';

const PUBLIC_ROUTE_NAMES = new Set([
  'login',
  'logout',
  'sharedchat',
  'widget-shared-chat',
  'not-found',
]);

function getHomeRoute(userRole, catalog, store) {
  // Resolve STUDENT wire value from server catalog; fall back to dashboard if unavailable
  const studentWire = catalog?.keyMap?.STUDENT;
  if (studentWire && normalizeRole(userRole, catalog) === studentWire) {
    const canUseResumeBuilder =
      store?.getters?.['auth/isFeatureEnabled']?.(AI_PACKAGE_FEATURE_KEYS.RESUME_BUILDER) === true &&
      store?.getters?.['auth/isFlagEnabled']?.(
        AI_PACKAGE_FEATURE_KEYS.RESUME_BUILDER,
        AI_PACKAGE_FLAGS.learnerTool,
      ) === true;
    return canUseResumeBuilder ? { name: 'resume-builder' } : { name: 'dashboard' };
  }
  return { name: 'dashboard' };
}

/**
 * Factory that produces a single beforeEach guard.
 * @param {import('vuex').Store} store
 */
export function createAuthGuard(store) {
  return (to, from, next) => {
    const isAuthenticated = store.getters['auth/isAuthenticated'];
    // Fallback to root store for legacy login flows; role must come from auth bootstrap or login
    const userRole = store.state.auth?.userRole || store.state.userRole || '';
    const catalog = store.state.auth?.roleCatalog;
    const isIframeRoute = to.path.startsWith('/iframe');
    const isPublic = to.meta.public === true || PUBLIC_ROUTE_NAMES.has(to.name);
    const isLoginPage = to.name === 'login';

    // 1. Public routes — always pass
    if (isPublic && !isLoginPage) { next(); return; }

    // 2. Iframe routes — own auth via query params
    if (isIframeRoute) { next(); return; }

    // 3. Login page — redirect away if already authenticated
    if (isLoginPage) {
      if (isAuthenticated) {
        next(getHomeRoute(userRole, catalog, store));
      } else {
        next();
      }
      return;
    }

    // 4. Auth check
    if (!isAuthenticated) {
      if (IframeUtil.isIframe()) {
        IframeUtil.postTokenExpired();
        return;
      }
      next({ name: 'login', query: { redirect: to.fullPath } });
      return;
    }

    // 5. Feature toggle — meta.requiredFeature
    const requiredFeature = to.matched.reduce(
      (feat, record) => record.meta.requiredFeature || feat,
      null,
    );
    const requiredFeatureFlag = to.matched.reduce(
      (flag, record) => record.meta.requiredFeatureFlag || flag,
      null,
    );
    if (requiredFeature) {
      if (!store.getters['auth/isFeatureEnabled'](requiredFeature)) {
        next(getHomeRoute(userRole, catalog, store));
        return;
      }
      if (requiredFeatureFlag) {
        if (!store.getters['auth/isFlagEnabled'](requiredFeature, requiredFeatureFlag)) {
          next(getHomeRoute(userRole, catalog, store));
          return;
        }
      }
    }

    // 6. RBAC — meta.requiredRole is a ROLE_KEY ('SUPER_ADMIN', 'INSTRUCTOR', …)
    //    rbac.hasMinRole / hasAdminAccess resolve keys → wire values via catalog.keyMap.
    const requiredRole = to.matched.reduce(
      (role, record) => record.meta.requiredRole || role,
      null,
    );
    if (requiredRole && catalog) {
      const aiAdmin = store.state.auth?.aiAdmin === true;
      let allowed;
      if (requiredRole === 'SUPER_ADMIN') {
        allowed = hasMinRole(userRole, requiredRole, catalog);
      } else if (requiredRole === 'INSTITUTION_ADMIN') {
        allowed =
          hasAdminAccess(aiAdmin, userRole, catalog) ||
          hasMinRole(userRole, requiredRole, catalog);
      } else {
        allowed = hasMinRole(userRole, requiredRole, catalog);
      }
      if (!allowed) {
        next({ name: 'not-found' });
        return;
      }
    }

    // 6b. Exact role check — meta.exactRole must match exactly.
    const exactRole = to.matched.reduce(
      (role, record) => record.meta.exactRole || role,
      null,
    );
    if (exactRole && catalog) {
      if (!hasRole(userRole, catalog, exactRole)) {
        next({ name: 'not-found' });
        return;
      }
    }

    // 7. All checks passed
    next();
  };
}
